/**
 * The DESIGN §6 lifecycle colour for a status, factored out of `ui/badge.tsx` so that
 * file exports components only — react-refresh/only-export-components needs that to
 * do its job, and a bar chart or legend swatch that wants the colour without a dot or
 * chip attached (`Ingest.tsx`'s runs table) has somewhere to get it from that isn't a
 * component module.
 *
 * Amber is waiting on a human, green has been through review, violet is folded into a
 * cluster, red failed, sky is mid-pipeline, grey is out of the bank.
 */
const TONE = {
  attention: "bg-bank-attention-wash text-bank-attention-ink",
  good: "bg-bank-good-wash text-bank-good-ink",
  dup: "bg-bank-dup-wash text-bank-dup-ink",
  bad: "bg-bank-bad-wash text-bank-bad-ink",
  pipe: "bg-bank-pipe-wash text-bank-pipe-ink",
  inert: "bg-bank-inert-wash text-bank-inert-ink",
} as const;

const DOT = {
  attention: "bg-bank-attention",
  good: "bg-bank-good",
  dup: "bg-bank-dup",
  bad: "bg-bank-bad",
  pipe: "bg-bank-pipe",
  inert: "bg-bank-inert",
} as const;

export type Tone = keyof typeof TONE;

/** Question statuses (§6), job statuses (§10.2), and the two dedup verdicts. */
const STATUS: Record<string, Tone> = {
  raw: "inert",
  extracted: "inert",
  enriched: "pipe",
  embedded: "pipe",
  dedup_checked: "pipe",
  pending_review: "attention",
  stale: "attention",
  approved: "good",
  duplicate: "dup",
  rejected: "inert",
  deprecated: "inert",
  failed: "bad",
  queued: "inert",
  running: "pipe",
  done: "good",
  cancelled: "inert",
  distinct: "inert",
};

export function statusTone(value: string): Tone {
  return STATUS[value] ?? "inert";
}

export function statusChipClass(value: string): string {
  return TONE[statusTone(value)];
}

/** The dot's fill class for a status, with no dot attached. */
export function statusFillClass(value: string): string {
  return DOT[statusTone(value)];
}
